import React, {useEffect} from 'react';
import {FormattedMessage} from "react-intl";
import { useNavigate } from 'react-router-dom';

import Styled from './styled';
import StyledContainer from "components/styled/StyledContainer";
import Input from "components/Input/Input";
import {useIntl} from "react-intl";
import {SearchOutlined} from "@ant-design/icons"
import debounce from 'lodash/debounce'
import { MarriageNOCCard, MarriageNOCCardSkeleton } from './MarriageNOC.Card';
import {STATUS} from "constants/status";
import MarriageNocDetailsSection from "containers/MarriageNOCDetails/MarriageNOCDetails.Section";
import orderBy from 'lodash/orderBy';
import Table from "components/Table/Table";
import {pathBuilder} from "routes/urlLocations";
import AppTable from "containers/styled/StyledTable";
import NOCsColumns from "containers/MarriageNOC/NOCs.Columns";
import {Skeleton} from "@/components/ui/skeleton";
import {marriageNOCStore} from "@/store/marriageNOCStore";

const CompletedNOCSkeleton = () => (
  <div className="space-y-4">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center space-x-4 p-4 border-b">
        <Skeleton className="h-4 w-[100px]" />
        <Skeleton className="h-4 w-[150px]" />
        <Skeleton className="h-4 w-[120px]" />
        <Skeleton className="h-4 w-[100px]" />
      </div>
    ))}
  </div>
);

const PendingNOCSection = ({ pendingNOCs, isLoading }) => {
  if (isLoading) {
    return (
      <MarriageNocDetailsSection title={"MarriageNOC.PendingSection"}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
          <MarriageNOCCardSkeleton />
          <MarriageNOCCardSkeleton />
        </div>
      </MarriageNocDetailsSection>
    );
  }

  if (!pendingNOCs || pendingNOCs.length === 0) {
    return null;
  }

  return (
    <MarriageNocDetailsSection title={"MarriageNOC.PendingSection"}>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-8">
        {orderBy(pendingNOCs, 'submitted_at', 'desc')?.map((marriageNoc) => (
          <MarriageNOCCard key={marriageNoc?.uuid} {...marriageNoc} />
        ))}
      </div>
    </MarriageNocDetailsSection>
  );
};

const CompletedNOCSection = ({ completedNOCs, isLoading }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <MarriageNocDetailsSection title={"MarriageNOC.Previous"}>
        <CompletedNOCSkeleton />
      </MarriageNocDetailsSection>
    );
  }

  if (!completedNOCs || completedNOCs.length === 0) {
    return null;
  }

  return (
    <MarriageNocDetailsSection title={"MarriageNOC.Previous"}>
      <AppTable>
        <Table
          dataSource={orderBy(completedNOCs, 'updated_at', 'desc')}
          columns={NOCsColumns()}
          onRow={({ uuid }) => ({
            onClick: () => navigate(pathBuilder.marriageNOCDetails(uuid))
          })}
        />
      </AppTable>
    </MarriageNocDetailsSection>
  );
};

const MarriageNoc = () => {
  const {formatMessage} = useIntl();
  
  // Get data from marriageNOCStore
  const pendingNOCs = marriageNOCStore.pendingNOCs.use();
  const completedNOCs = marriageNOCStore.completedNOCs.use();
  const isLoadingPending = marriageNOCStore.isLoadingPending.use();
  const isLoadingCompleted = marriageNOCStore.isLoadingCompleted.use();

  const handleSearch = (value) => {
    if (!value || value.trim() === '') {
      // If search is empty, fetch both pending and completed NOCs
      marriageNOCStore.fetchPendingNOCs();
      marriageNOCStore.fetchCompletedNOCs();
    } else {
      // If search has value, perform search
      marriageNOCStore.searchMarriageNoc(value);
    }
  };

  const debouncedSearch = debounce(handleSearch, 500);

  // Fetch data on component mount
  useEffect(() => {
    marriageNOCStore.fetchPendingNOCs();
    marriageNOCStore.fetchCompletedNOCs();
  }, []);

  return (
    <Styled.Page>
      <StyledContainer>
        <Styled.Title>
          <FormattedMessage id={"MarriageNOC.Title"}/>
        </Styled.Title>
        <div className={"flex justify-between mt-8"}>
          <Styled.SearchField>
            <Input
              onChange={({target}) => debouncedSearch(target?.value)}
              prefix={
                <SearchOutlined style={{fontSize: 16}}/>
              }
              placeholder={formatMessage({id: "MarriageNOC.SearchPlaceholder"})}
            />
          </Styled.SearchField>
        </div>
        <PendingNOCSection 
          pendingNOCs={pendingNOCs} 
          isLoading={isLoadingPending} 
        />
        <CompletedNOCSection 
          completedNOCs={completedNOCs}
          isLoading={isLoadingCompleted}
        />
      </StyledContainer>
    </Styled.Page>
  );
};

export default MarriageNoc;
